/**
 * Image Generator Header Component
 *
 * Renders the top header bar of the image generator, including:
 * - Sidebar toggle button
 * - Title and info link
 * - Instance toggle buttons (1/2/3)
 * - Presets button
 */

import { Menu, Info, BookMarked, FolderOpen, Eraser, Library } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ImageInstanceConfig, NUM_INSTANCES } from '../types/image-generator.types';
import type { ImageMode } from '../types/image-generator-modes';
import { ModeSelector } from './ModeSelector';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { ArcheExpandButton } from '../../../components/ArcheExpandButton';
import { archeInfoPath } from '@/lib/arche-url';

export interface ImageGeneratorHeaderProps {
  isCollapsed: boolean;
  onToggleSidebar: () => void;
  enabledInstances: boolean[];
  minimizedInstances: boolean[];
  instanceConfigs: ImageInstanceConfig[];
  onToggleInstance: (idx: number) => void;
  onToggleMinimized: (idx: number) => void;
  canMinimize: (idx: number) => boolean;
  onOpenPresets: () => void;
  activeMode: ImageMode | null;
  modes: ImageMode[];
  onSelectMode: (mode: ImageMode) => void;
  onOpenModes: () => void;
  onClearAllImages: () => void;
}

export function ImageGeneratorHeader({
  isCollapsed,
  onToggleSidebar,
  enabledInstances,
  minimizedInstances,
  instanceConfigs,
  onToggleInstance,
  onToggleMinimized,
  canMinimize,
  onOpenPresets,
  activeMode,
  modes,
  onSelectMode,
  onOpenModes,
  onClearAllImages,
}: ImageGeneratorHeaderProps) {
  return (
    <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
      <div className="px-4 py-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="icon"
            onClick={onToggleSidebar}
            className={`h-11 w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
            title="Open sidebar"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <h1 className="text-lg font-semibold flex-1 min-w-0">Image Generator</h1>
          <FavoriteButton archeId="arche_image_generator" className="flex-shrink-0" />
          <Link
            to={archeInfoPath('arche_image_generator')}
            className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
            title="View arche details"
          >
            <Info className="h-6 w-6" />
          </Link>
          <Link
            to="/a/arche_image_generator/models"
            className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
            title="Browse all models"
          >
            <Library className="h-6 w-6" />
          </Link>
          {/* Path-keyed deep link into My Data — `/Arche Saves/AI Images` is the
              canonical system folder for AI-generated images (mirror of
              VAULT_SYSTEM_FOLDER_PATHS.AI_IMAGES in @fias/db-types). MyDataPage
              resolves path → folderId from the already-loaded folders query, so
              the URL is Holder-independent and shareable. */}
          <Link
            to={`/my-data?folderPath=${encodeURIComponent('/Arche Saves/AI Images')}`}
            className="ml-2 text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
            title="View AI Images folder"
          >
            <FolderOpen className="h-6 w-6" />
          </Link>
          <ArcheExpandButton archeId="arche_image_generator" className="flex-shrink-0" />
        </div>

        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
          <div className="flex items-center gap-1">
            {Array.from({ length: NUM_INSTANCES }, (_, idx) => {
              const enabled = enabledInstances[idx];
              const minimized = minimizedInstances[idx];

              // Disabled: ghost + dim, click to enable
              // Enabled + minimized: outline, click to expand
              // Enabled + expanded: default (solid), click to minimize
              const variant = !enabled ? 'ghost' : minimized ? 'outline' : 'default';
              const handleClick = () => {
                if (!enabled) {
                  onToggleInstance(idx);
                } else {
                  onToggleMinimized(idx);
                }
              };
              // Right-click to disable an enabled panel (only for optional panels 4+)
              const handleContextMenu = (e: React.MouseEvent) => {
                if (idx >= 3 && enabled) {
                  e.preventDefault();
                  onToggleInstance(idx);
                }
              };
              const isDisabled = enabled && !minimized && !canMinimize(idx);
              const title = !enabled
                ? `Enable panel ${idx + 1}`
                : idx >= 3
                  ? minimized
                    ? `Expand panel ${idx + 1} (right-click to disable)`
                    : `Collapse panel ${idx + 1} (right-click to disable)`
                  : minimized
                    ? `Expand ${instanceConfigs[idx].presetName}`
                    : `Collapse ${instanceConfigs[idx].presetName}`;

              return (
                <Button
                  key={idx}
                  variant={variant}
                  size="sm"
                  className={`h-8 w-8 p-0 text-xs ${!enabled ? 'opacity-40' : ''}`}
                  onClick={handleClick}
                  onContextMenu={handleContextMenu}
                  disabled={isDisabled}
                  title={title}
                >
                  {idx + 1}
                </Button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={onClearAllImages}
            title="Clear all images"
          >
            <Eraser className="h-4 w-4" />
            <span className="hidden sm:inline">Clear</span>
          </Button>
          <ModeSelector
            activeMode={activeMode}
            modes={modes}
            onSelectMode={onSelectMode}
            onOpenModes={onOpenModes}
          />
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={onOpenPresets}
            title="View Presets"
          >
            <BookMarked className="h-4 w-4" />
            <span className="hidden sm:inline">Presets</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
